import React, { useMemo, useState } from 'react';

// Keeps the first item for each key, so a list behaves as a set keyed by getKey.
const uniqueBy = (items, getKey) => {
  const seen = new Set();
  return items.filter((item) => {
    const key = getKey(item);
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};

const Panel = ({ title, items, selected, onToggle, onSelectAll, search, onSearch, getKey, getLabel, onItemDoubleClick }) => {
  const filtered = useMemo(
    () => items.filter((item) => getLabel(item).toLowerCase().includes(search.toLowerCase())),
    [items, search, getLabel]
  );
  const allSelected = filtered.length > 0 && filtered.every((item) => selected.has(getKey(item)));

  return (
    <div className="flex flex-col flex-1 min-w-0 border border-slate-300 rounded-xl bg-white">
      <div className="flex items-center justify-between px-3 py-2 border-b border-slate-200">
        <span className="text-xs font-bold text-slate-700">{title} ({items.length})</span>
        <label className="flex items-center gap-1 text-xs text-slate-500 cursor-pointer">
          <input
            type="checkbox"
            className="checkbox checkbox-xs"
            checked={allSelected}
            onChange={() => onSelectAll(filtered, !allSelected)}
          />{' '}
          All
        </label>
      </div>
      <div className="px-3 py-2">
        <input
          type="text"
          className="input input-bordered input-sm w-full rounded-lg"
          placeholder="Search..."
          value={search}
          onChange={(e) => onSearch(e.target.value)}
        />
      </div>
      <ul role="listbox" aria-multiselectable="true" className="flex-1 overflow-y-auto px-1 pb-2" style={{ minHeight: '14rem', maxHeight: '14rem' }}>
        {filtered.length === 0 && <li className="text-xs text-slate-400 text-center py-6">No items</li>}
        {filtered.map((item) => {
          const key = getKey(item);
          const isSelected = selected.has(key);
          return (
            <li
              key={key}
              role="option"
              aria-selected={isSelected}
              tabIndex={0}
              onClick={() => onToggle(key)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  onToggle(key);
                }
              }}
              onDoubleClick={() => onItemDoubleClick(item)}
              className={`flex items-center gap-2 px-2 py-1.5 rounded-lg text-sm cursor-pointer select-none ${isSelected ? 'bg-primary/10 text-primary' : 'hover:bg-slate-100'}`}
            >
              <input type="checkbox" className="checkbox checkbox-xs" checked={isSelected} readOnly />
              <span className="truncate">{getLabel(item)}</span>
            </li>
          );
        })}
      </ul>
    </div>
  );
};

/**
 * Dual-panel selector. `assigned` is an ordered array; its order is the order the
 * parent receives through `onChange(newAssigned)`.
 */
const DualListbox = ({
  available: availableProp,
  assigned: assignedProp,
  onChange,
  getKey = (item) => item.id,
  getLabel = (item) => item.label,
  availableTitle = 'Available',
  assignedTitle = 'Assigned',
}) => {
  const assigned = useMemo(() => uniqueBy(assignedProp, getKey), [assignedProp]); // eslint-disable-line react-hooks/exhaustive-deps
  const available = useMemo(() => {
    const assignedKeys = new Set(assigned.map(getKey));
    return uniqueBy(availableProp, getKey).filter((item) => !assignedKeys.has(getKey(item)));
  }, [availableProp, assigned]); // eslint-disable-line react-hooks/exhaustive-deps

  const [availableSelected, setAvailableSelected] = useState(new Set());
  const [assignedSelected, setAssignedSelected] = useState(new Set());
  const [availableSearch, setAvailableSearch] = useState('');
  const [assignedSearch, setAssignedSearch] = useState('');

  const toggle = (setter) => (key) =>
    setter((prev) => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });

  const selectAll = (setter) => (visibleItems, checked) =>
    setter((prev) => {
      const next = new Set(prev);
      visibleItems.forEach((item) => (checked ? next.add(getKey(item)) : next.delete(getKey(item))));
      return next;
    });

  const add = (items) => {
    if (items.length === 0) return;
    onChange(uniqueBy([...assigned, ...items], getKey));
    setAvailableSelected(new Set());
  };

  const remove = (items) => {
    if (items.length === 0) return;
    const keys = new Set(items.map(getKey));
    onChange(assigned.filter((item) => !keys.has(getKey(item))));
    setAssignedSelected(new Set());
  };

  // Moves every selected item one step, keeping their relative order and stopping at the edges.
  const move = (direction) => {
    const next = [...assigned];
    const indexes = next.map((_, i) => i).filter((i) => assignedSelected.has(getKey(next[i])));
    if (direction > 0) indexes.reverse();
    indexes.forEach((i) => {
      const target = i + direction;
      if (target < 0 || target >= next.length || assignedSelected.has(getKey(next[target]))) return;
      [next[i], next[target]] = [next[target], next[i]];
    });
    onChange(next);
  };

  const btn = 'btn btn-sm btn-outline btn-square';

  return (
    <div className="flex flex-col md:flex-row gap-3 items-stretch">
      <Panel
        title={availableTitle}
        items={available}
        selected={availableSelected}
        onToggle={toggle(setAvailableSelected)}
        onSelectAll={selectAll(setAvailableSelected)}
        search={availableSearch}
        onSearch={setAvailableSearch}
        getKey={getKey}
        getLabel={getLabel}
        onItemDoubleClick={(item) => add([item])}
      />

      <div className="flex md:flex-col justify-center gap-2">
        <button type="button" className={btn} title="Add selected" disabled={availableSelected.size === 0}
          onClick={() => add(available.filter((i) => availableSelected.has(getKey(i))))}>
          <i className="pi pi-angle-right"></i>
        </button>
        <button type="button" className={btn} title="Add all" disabled={available.length === 0}
          onClick={() => add(available)}>
          <i className="pi pi-angle-double-right"></i>
        </button>
        <button type="button" className={btn} title="Remove selected" disabled={assignedSelected.size === 0}
          onClick={() => remove(assigned.filter((i) => assignedSelected.has(getKey(i))))}>
          <i className="pi pi-angle-left"></i>
        </button>
        <button type="button" className={btn} title="Remove all" disabled={assigned.length === 0}
          onClick={() => remove(assigned)}>
          <i className="pi pi-angle-double-left"></i>
        </button>
      </div>

      <Panel
        title={assignedTitle}
        items={assigned}
        selected={assignedSelected}
        onToggle={toggle(setAssignedSelected)}
        onSelectAll={selectAll(setAssignedSelected)}
        search={assignedSearch}
        onSearch={setAssignedSearch}
        getKey={getKey}
        getLabel={getLabel}
        onItemDoubleClick={(item) => remove([item])}
      />

      <div className="flex md:flex-col justify-center gap-2">
        <button type="button" className={btn} title="Move up" disabled={assignedSelected.size === 0}
          onClick={() => move(-1)}>
          <i className="pi pi-angle-up"></i>
        </button>
        <button type="button" className={btn} title="Move down" disabled={assignedSelected.size === 0}
          onClick={() => move(1)}>
          <i className="pi pi-angle-down"></i>
        </button>
      </div>
    </div>
  );
};

export default DualListbox;
